import { Link } from "react-router-dom";
import { Truck, ShieldCheck, FileText, Calculator, Bell, ArrowRight, Calendar, ListChecks } from "lucide-react";

const HERO = "https://images.pexels.com/photos/12261472/pexels-photo-12261472.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ACCENT = "https://images.pexels.com/photos/34106714/pexels-photo-34106714.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Nav */}
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/logo.png" alt="Crystal Logistics Services" className="h-16 w-auto" data-testid="landing-logo" />
          <div className="flex items-center gap-2">
            <Link to="/login" className="hs-btn-ghost text-sm" data-testid="landing-signin">SIGN IN</Link>
            <Link to="/register" className="hs-btn-primary" data-testid="landing-signup">GET STARTED</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-blueprint">
        <img src={HERO} className="absolute inset-0 w-full h-full object-cover opacity-10" alt=""/>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-100/60" />
        <div className="hs-grid-bg absolute inset-0 opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="hs-stripe" />
            <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Safety &amp;<br/> Compliance,<br/>
              <span className="text-blue-700">Managed.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-500 max-w-2xl leading-relaxed">
              The all-in-one safety &amp; compliance portal for U.S. trucking companies. Track IRP, IFTA, UCR and MCS-150.
              Store every CDL, insurance, and registration. Calculate quarterly fuel tax in seconds.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" className="hs-btn-primary inline-flex items-center gap-2" data-testid="hero-cta-signup">
                REQUEST ACCESS <ArrowRight size={16}/>
              </Link>
              <Link to="/login" className="hs-btn-secondary" data-testid="hero-cta-signin">SIGN IN</Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <Stat label="FILINGS" value="16 TRACKED"/>
              <Stat label="STATES" value="48 USA"/>
              <Stat label="UPTIME" value="99.9%"/>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:block">
            <div className="hs-card overflow-hidden">
              <img src={ACCENT} alt="" className="w-full h-64 object-cover" />
              <div className="p-5">
                <div className="hs-overline">LIVE STATUS</div>
                <h4 className="font-display font-bold text-lg mt-1">Operating across 48 states</h4>
                <p className="text-xs text-slate-500 mt-2 font-mono">REAL-TIME MONITORING · ALERTS · PDF REPORTS</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="hs-overline text-blue-700">CAPABILITIES</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-2 max-w-3xl">Everything you need.<br/>Nothing you don't.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Feature icon={ShieldCheck} title="Filing Tracker" body="IRP, IFTA, UCR, MCS-150 statuses with timeline + reminders."/>
            <Feature icon={FileText} title="Document Vault" body="CDLs, insurance, IRP cards, registrations — all secured."/>
            <Feature icon={Calculator} title="IFTA Calculator" body="Per-state miles & fuel — quarterly tax owed instantly."/>
            <Feature icon={Bell} title="Smart Alerts" body="Email + in-app notifications for every deadline & approval."/>
            <Feature icon={Calendar} title="Deadline Radar" body="See expiring filings 30 / 60 / 90 days out."/>
            <Feature icon={ListChecks} title="Approval Workflow" body="Admin approves or rejects every uploaded document."/>
            <Feature icon={Truck} title="Multi-Vehicle" body="Plans up to 100 power units per fleet."/>
            <Feature icon={ShieldCheck} title="PDF Reports" body="Audit-ready compliance & IFTA exports."/>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="hs-stripe mx-auto" />
          <h3 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">Ready to roll?</h3>
          <p className="mt-4 text-slate-500">Built for owner-operators and fleet managers. No CDL course required.</p>
          <Link to="/register" className="hs-btn-primary inline-flex items-center gap-2 mt-8" data-testid="bottom-cta">
            CREATE YOUR ACCOUNT <ArrowRight size={16}/>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-xs text-slate-500 font-mono">
          <span>© {new Date().getFullYear()} CRYSTAL LOGISTICS</span>
          <span>BUILT FOR US TRUCKING · v1.0</span>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="hs-card hs-card-hover p-6">
      <div className="w-10 h-10 border border-slate-200 flex items-center justify-center"><Icon size={18} className="text-blue-700"/></div>
      <h4 className="font-display font-bold text-lg mt-4 uppercase tracking-tight">{title}</h4>
      <p className="text-sm text-slate-500 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="hs-overline">{label}</div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
    </div>
  );
}
